/**
 * Turning a safety event's stored record into sentences a person reads.
 *
 * The first version of this screen printed the raw record — `"cause":
 * "pre_session_red"`, `"symptomDeltaTrigger": 2` — on the reasoning that the
 * doctor should see everything and the software should not decide which part of
 * a clinical picture he is allowed. The first half of that is right. The second
 * half was a developer's answer to a question nobody asked: formatting is not
 * discarding, and a clinician reading his own patient's chart should not have
 * to parse JSON to find out what happened.
 *
 * Everything in the record is still shown. It is shown in English.
 *
 * WHY THIS IS NOT ON THE SERVER. The stored record stays raw, so a later screen,
 * a report, or a question nobody has asked yet can read the same event
 * differently. Rendering is a property of the screen; the record is the record.
 *
 * WHY THE RATING WORDS COME FROM THE PATIENT'S OWN COPY. "Not good" is what the
 * patient tapped. If this screen said "red" or "3" or "severe", the doctor and
 * the patient would be using different words for the same answer, and the
 * conversation at the next visit is between those two people.
 */

import { METRIC_LABELS, METRIC_UNITS, type MetricKey } from '@pathways/exercises';
import { RATING_OPTIONS } from '../therapy/copy.js';

export interface TraceLine {
  label: string;
  value: string;
}

/** The patient's own word for an answer. Falls back rather than inventing one. */
function ratingWord(value: unknown): string | null {
  if (typeof value !== 'number') return null;
  return RATING_OPTIONS.find((option) => option.value === value)?.label ?? null;
}

/** Numbers a person reads: whole where whole is honest, one decimal otherwise. */
function amount(value: number, metric: MetricKey): string {
  const unit = METRIC_UNITS[metric] ?? '';
  const rounded = Math.abs(value) >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  if (!unit) return String(rounded);
  // A per-cent sign closes up against its number; every other unit takes a
  // space. "94 %" is the kind of thing that makes a screen look machine-made.
  return unit === '%' ? `${rounded}%` : `${rounded} ${unit}`;
}

const percent = (ratio: number): string => `${Math.round(Math.abs(ratio) * 100)}%`;

/** m:ss into the exercise, which is the form a duration of this size is read in. */
function elapsed(ms: number): string {
  const whole = Math.round(ms / 1000);
  const minutes = Math.floor(whole / 60);
  const seconds = whole % 60;
  if (minutes === 0) return `${seconds} seconds`;
  return `${minutes} min ${String(seconds).padStart(2, '0')} s`;
}

interface DecaySignalRecord {
  metric: MetricKey;
  direction: 'increase' | 'decrease';
  baseline: number;
  recent: number;
  degradation: number;
}

/**
 * One line per thing the record actually contains. Nothing is invented when a
 * field is absent — a pre-session block has no performance trace, and a line
 * saying so would be noise on every one of them.
 */
export function describeTrigger(data: unknown): TraceLine[] {
  if (typeof data !== 'object' || data === null) return [];
  const record = data as Record<string, unknown>;
  const lines: TraceLine[] = [];

  const preSession = ratingWord(record.preSessionRating);
  if (preSession) lines.push({ label: 'How they were before starting', value: preSession });

  const delayedOnset = ratingWord(record.delayedOnsetRating);
  if (delayedOnset) {
    lines.push({ label: 'The hours after their last session', value: delayedOnset });
  }

  if (Array.isArray(record.earlierTodayRatings) && record.earlierTodayRatings.length > 0) {
    const words = record.earlierTodayRatings.map((r) => ratingWord(r) ?? '—');
    lines.push({ label: 'Earlier today they reported', value: words.join(', ') });
  }

  const closeOut = ratingWord(record.closeOutRating);
  if (closeOut) lines.push({ label: 'How they felt afterwards', value: closeOut });

  /**
   * The setting that was in force, in words rather than as a number. "2" means
   * nothing on its own, and the doctor set it in a dropdown that said this.
   */
  if (typeof record.symptomDeltaTrigger === 'number') {
    lines.push({
      label: 'Their setting at the time',
      value:
        record.symptomDeltaTrigger === 1
          ? 'end the session on any step worse than they started'
          : 'end the session two steps worse than they started',
    });
  }

  lines.push(...describeDecay(record.decayTrace));

  return lines;
}

/** §9 Layer 3's trace: what the monitor compared, and against what. */
function describeDecay(trace: unknown): TraceLine[] {
  if (typeof trace !== 'object' || trace === null) return [];
  const record = trace as Record<string, unknown>;
  const lines: TraceLine[] = [];

  if (Array.isArray(record.signals)) {
    for (const raw of record.signals as DecaySignalRecord[]) {
      if (!raw || typeof raw.baseline !== 'number' || typeof raw.recent !== 'number') continue;
      const name = METRIC_LABELS[raw.metric] ?? raw.metric;
      // "Worse" is the exercise's own judgment, declared in its decay
      // signature. A rising reaction time is worse; a rising accuracy is not.
      const worse = raw.degradation > 0;
      const direction = raw.direction === 'increase' ? 'higher' : 'lower';
      lines.push({
        label: name,
        value:
          `${amount(raw.baseline, raw.metric)} at the start, ` +
          `${amount(raw.recent, raw.metric)} by the end — ` +
          `${percent(raw.degradation)} ${worse ? direction : 'better'}`,
      });
    }
  }

  if (typeof record.overall === 'number' && typeof record.threshold === 'number') {
    lines.push({
      label: 'Overall',
      value:
        `${percent(record.overall)} down on their own opening, ` +
        `against a limit of ${percent(record.threshold)}`,
    });
  }

  if (typeof record.atMs === 'number') {
    lines.push({ label: 'Stopped', value: `${elapsed(record.atMs)} into the exercise` });
  }

  return lines;
}
